export interface PartnerPhotoPerson {
  partner_number: number
  person_id: number
  first_name: string
  last_name: string
  display_name: string
  photo_path: string | null
  inferred_storage_path: string | null
  has_storage_object: boolean
  photo_url: string | null
}

function getSupabaseFunctionsBaseUrl(): string {
  const environment = String(import.meta.env.ENVIRONMENT || 'dev').toLowerCase()
  const isLocalDev = import.meta.env.DEV

  const supabaseUrl = !isLocalDev && import.meta.env.VITE_SUPABASE_URL
    ? import.meta.env.VITE_SUPABASE_URL
    : environment === 'prod'
      ? import.meta.env.PROD_SUPABASE_URL
      : import.meta.env.DEV_SUPABASE_URL

  if (!supabaseUrl) {
    throw new Error('SUPABASE_URL_MISSING')
  }

  return `${String(supabaseUrl).replace(/\/+$/, '')}/functions/v1/partner-photos`
}

async function readError(response: Response): Promise<string> {
  try {
    const payload = await response.json() as { error?: string; detail?: string }
    return payload.error || payload.detail || `HTTP ${response.status}`
  } catch {
    return `HTTP ${response.status}`
  }
}

async function partnerPhotosFetch(path: string, init?: RequestInit): Promise<Response> {
  const token = localStorage.getItem('auth_token')
  if (!token) {
    throw new Error('AUTH_TOKEN_MISSING')
  }

  const headers = new Headers(init?.headers)
  headers.set('Authorization', `Bearer ${token}`)

  const response = await fetch(`${getSupabaseFunctionsBaseUrl()}${path}`, {
    ...init,
    headers,
  })

  if (!response.ok) {
    throw new Error(await readError(response))
  }

  return response
}

export async function listPartnerPhotoPeople(partnerNumbers: number[]): Promise<PartnerPhotoPerson[]> {
  const uniqueNumbers = Array.from(new Set(partnerNumbers.filter(number => Number.isFinite(number) && number > 0)))
  if (uniqueNumbers.length === 0) return []

  const query = uniqueNumbers.map(String).join(',')
  const response = await partnerPhotosFetch(`/people?partner_numbers=${encodeURIComponent(query)}`)
  const payload = await response.json() as { people?: PartnerPhotoPerson[] }
  return payload.people ?? []
}

export async function uploadPartnerPersonPhoto(params: {
  partnerNumber: number
  personId: number
  file: File
}): Promise<PartnerPhotoPerson> {
  const formData = new FormData()
  formData.append('partner_number', String(params.partnerNumber))
  formData.append('person_id', String(params.personId))
  formData.append('file', params.file)

  const response = await partnerPhotosFetch('/upload', {
    method: 'POST',
    body: formData,
  })
  const payload = await response.json() as { person: PartnerPhotoPerson }
  return payload.person
}

export async function deletePartnerPersonPhoto(params: {
  partnerNumber: number
  personId: number
  path?: string | null
}): Promise<PartnerPhotoPerson> {
  const response = await partnerPhotosFetch('/photo', {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      partner_number: params.partnerNumber,
      person_id: params.personId,
      path: params.path ?? null,
    }),
  })
  const payload = await response.json() as { person: PartnerPhotoPerson }
  return payload.person
}

export async function linkPartnerPersonPhoto(params: {
  partnerNumber: number
  personId: number
  path: string
}): Promise<PartnerPhotoPerson> {
  const response = await partnerPhotosFetch('/link', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      partner_number: params.partnerNumber,
      person_id: params.personId,
      path: params.path,
    }),
  })
  const payload = await response.json() as { person: PartnerPhotoPerson }
  return payload.person
}
